import React, { useState } from 'react';
import { QUESTS } from '@/data/quests';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const QuestLog: React.FC = () => {
  const [expandedQuest, setExpandedQuest] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');

  const activeQuests = QUESTS.filter((q) => !q.completed);
  const completedQuests = QUESTS.filter((q) => q.completed);
  const displayQuests = activeTab === 'active' ? activeQuests : completedQuests;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-400';
      case 'medium':
        return 'text-yellow-400';
      case 'hard':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <Card className="bg-slate-800 border-orange-500 p-4 h-full overflow-auto">
      <div className="space-y-4">
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setActiveTab('active')}
            variant={activeTab === 'active' ? 'default' : 'outline'}
            className={`flex-1 ${
              activeTab === 'active'
                ? 'bg-orange-600 border-orange-600'
                : 'border-gray-600 text-gray-300'
            }`}
          >
            Active ({activeQuests.length})
          </Button>
          <Button
            onClick={() => setActiveTab('completed')}
            variant={activeTab === 'completed' ? 'default' : 'outline'}
            className={`flex-1 ${
              activeTab === 'completed'
                ? 'bg-orange-600 border-orange-600'
                : 'border-gray-600 text-gray-300'
            }`}
          >
            Completed ({completedQuests.length})
          </Button>
        </div>

        <div className="space-y-2">
          {displayQuests.length === 0 ? (
            <p className="text-gray-400 text-center py-4">
              {activeTab === 'active' ? 'No active quests' : 'No completed quests'}
            </p>
          ) : (
            displayQuests.map((quest) => (
              <div key={quest.id} className="border border-gray-600 rounded overflow-hidden">
                <button
                  onClick={() =>
                    setExpandedQuest(expandedQuest === quest.id ? null : quest.id)
                  }
                  className="w-full bg-slate-700 hover:bg-slate-600 p-3 flex justify-between items-center transition text-left"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-white">{quest.title}</p>
                    <p className={`text-sm font-bold ${getDifficultyColor(quest.difficulty)}`}>
                      {quest.difficulty.toUpperCase()} • {quest.giver}
                    </p>
                  </div>
                  {expandedQuest === quest.id ? (
                    <ChevronUp className="text-orange-500" />
                  ) : (
                    <ChevronDown className="text-orange-500" />
                  )}
                </button>

                {expandedQuest === quest.id && (
                  <div className="bg-slate-800 p-3 border-t border-gray-600 space-y-3">
                    <div>
                      <p className="text-gray-300 text-sm">{quest.description}</p>
                    </div>

                    <div>
                      <p className="text-orange-400 font-semibold text-sm mb-2">Objectives:</p>
                      <ul className="space-y-1">
                        {quest.objectives.map((obj, idx) => (
                          <li key={idx} className="text-gray-300 text-sm flex items-start">
                            <span className="mr-2">•</span>
                            {obj}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="text-orange-400 font-semibold text-sm mb-2">Rewards:</p>
                      <div className="space-y-1 text-sm text-gray-300">
                        <p>
                          <span className="text-yellow-400">Experience:</span> +{quest.rewards.experience} XP
                        </p>
                        <p>
                          <span className="text-yellow-400">Gold:</span> +{quest.rewards.gold} gold
                        </p>
                        {quest.rewards.romanceBonus && (
                          <p>
                            <span className="text-pink-400">Romance:</span> +{quest.rewards.romanceBonus}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
};
